
import { supabase } from "@/integrations/supabase/client";
import { CategoryDefinition, TypeDefinition, SortOption, UserFilterPreference, CountryData, CountryDefaultPreference, CatalogDefaultAudience } from "@/types/filter";

export async function fetchCountries(): Promise<CountryData[]> {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('id, name, name_ar, name_tr, description, image_url, display_order')
      .order('display_order', { ascending: true });

    if (error) {
      console.error("Error fetching countries:", error);
      throw error;
    }

    // Map the data to ensure proper field names
    return data.map(country => ({
      id: country.id,
      name: country.name,
      name_ar: country.name_ar || null,
      name_tr: country.name_tr || null,
      description: country.description || '',
      imageUrl: country.image_url || null,
      display_order: country.display_order
    }));
  } catch (error) {
    console.error("Error in fetchCountries:", error);
    return [];
  }
}

export async function fetchCountryById(countryId: string): Promise<CountryData | null> {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('*')
      .eq('id', countryId)
      .single();

    if (error) {
      console.error(`Error fetching country with ID ${countryId}:`, error);
      return null;
    }

    return data as CountryData;
  } catch (error) {
    console.error("Error in fetchCountryById:", error);
    return null;
  }
}

export async function fetchCountryByName(countryName: string): Promise<CountryData | null> {
  try {
    const { data, error } = await supabase
      .from('countries')
      .select('*')
      .eq('name', countryName)
      .single();

    if (error) {
      console.error(`Error fetching country with name ${countryName}:`, error);
      return null;
    }

    return data as CountryData;
  } catch (error) {
    console.error("Error in fetchCountryByName:", error);
    return null;
  }
}

export async function fetchCategoriesByCountryId(countryId: string): Promise<CategoryDefinition[]> {
  try {
    const { data, error } = await supabase
      .from('banknote_category_definitions')
      .select('*')
      .eq('country_id', countryId)
      .order('display_order', { ascending: true });

    if (error) {
      console.error(`Error fetching categories for country ID ${countryId}:`, error);
      throw error;
    }

    return data as CategoryDefinition[];
  } catch (error) {
    console.error("Error in fetchCategoriesByCountryId:", error);
    return [];
  }
}

export async function fetchTypesByCountryId(countryId: string): Promise<TypeDefinition[]> {
  try {
    const { data, error } = await supabase
      .from('banknote_type_definitions')
      .select('*')
      .eq('country_id', countryId)
      .order('display_order', { ascending: true });

    if (error) {
      console.error(`Error fetching types for country ID ${countryId}:`, error);
      throw error;
    }

    return data as TypeDefinition[];
  } catch (error) {
    console.error("Error in fetchTypesByCountryId:", error);
    return [];
  }
}

export async function fetchSortOptionsByCountryId(countryId: string): Promise<SortOption[]> {
  try {
    const { data, error } = await supabase
      .from('banknote_sort_options')
      .select('*')
      .eq('country_id', countryId)
      .order('display_order', { ascending: true });

    if (error) {
      console.error(`Error fetching sort options for country ID ${countryId}:`, error);
      throw error;
    }

    return data as SortOption[];
  } catch (error) {
    console.error("Error in fetchSortOptionsByCountryId:", error);
    return [];
  }
}

export async function fetchUserFilterPreferences(userId: string, countryId: string): Promise<UserFilterPreference | null> {
  try {
    const { data, error } = await supabase
      .from('user_filter_preferences')
      .select('*')
      .eq('user_id', userId)
      .eq('country_id', countryId)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching user filter preferences for user ID ${userId} and country ID ${countryId}:`, error);
      return null;
    }
    console.log("User filter preferences:", data);
    return data as UserFilterPreference;
  } catch (error) {
    console.error("Error in fetchUserFilterPreferences:", error);
    return null;
  }
}

export async function saveUserFilterPreferences(
  userId: string,
  countryId: string,
  preferences: {
    selected_categories: string[];
    selected_types: string[];
    selected_sort_options: string[];
    group_mode: boolean;
    view_mode?: 'grid' | 'list';
    images_only?: boolean;
  }
): Promise<void> {
  try {
    const row: Record<string, unknown> = {
      user_id: userId,
      country_id: countryId,
      selected_categories: preferences.selected_categories,
      selected_types: preferences.selected_types,
      selected_sort_options: preferences.selected_sort_options,
      group_mode: preferences.group_mode,
      view_mode: preferences.view_mode || 'grid',
      updated_at: new Date().toISOString(),
    };
    // Only set images_only when explicitly provided so partial saves (e.g. from
    // category/sort changes) don't overwrite an existing preference.
    if (typeof preferences.images_only === 'boolean') {
      row.images_only = preferences.images_only;
    }

    const { data, error } = await supabase
      .from('user_filter_preferences')
      .upsert(row, { onConflict: 'user_id,country_id' });

    if (error) {
      console.error("Error saving user filter preferences:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in saveUserFilterPreferences:", error);
  }
}

export async function fetchCountryDefaultPreferences(
  countryId: string,
  audience: CatalogDefaultAudience
): Promise<CountryDefaultPreference | null> {
  try {
    const { data, error } = await supabase
      .from('country_default_preferences')
      .select('*')
      .eq('country_id', countryId)
      .eq('audience', audience)
      .maybeSingle();

    if (error) {
      console.error(`Error fetching country defaults for ${countryId}/${audience}:`, error);
      return null;
    }
    return data as CountryDefaultPreference | null;
  } catch (error) {
    console.error("Error in fetchCountryDefaultPreferences:", error);
    return null;
  }
}

export async function fetchAllCountryDefaultPreferences(
  countryId: string
): Promise<{ anonymous: CountryDefaultPreference | null; new_user: CountryDefaultPreference | null }> {
  try {
    const { data, error } = await supabase
      .from('country_default_preferences')
      .select('*')
      .eq('country_id', countryId);

    if (error) {
      console.error(`Error fetching all country defaults for ${countryId}:`, error);
      return { anonymous: null, new_user: null };
    }

    const rows = (data || []) as CountryDefaultPreference[];
    return {
      anonymous: rows.find(r => r.audience === 'anonymous') || null,
      new_user: rows.find(r => r.audience === 'new_user') || null,
    };
  } catch (error) {
    console.error("Error in fetchAllCountryDefaultPreferences:", error);
    return { anonymous: null, new_user: null };
  }
}

export async function saveCountryDefaultPreferences(
  countryId: string,
  audience: CatalogDefaultAudience,
  payload: {
    group_mode: boolean;
    view_mode: 'grid' | 'list';
    images_only: boolean;
    selected_categories: string[];
    selected_types: string[];
    selected_sort_options: string[];
  }
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const row = {
      country_id: countryId,
      audience,
      group_mode: payload.group_mode,
      view_mode: payload.view_mode,
      images_only: payload.images_only,
      selected_categories: payload.selected_categories,
      selected_types: payload.selected_types,
      selected_sort_options: payload.selected_sort_options,
      updated_at: new Date().toISOString(),
      updated_by: user?.id ?? null,
    };

    const { error } = await supabase
      .from('country_default_preferences')
      .upsert(row, { onConflict: 'country_id,audience' });

    if (error) {
      console.error("Error saving country default preferences:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in saveCountryDefaultPreferences:", error);
    throw error;
  }
}

// Overwrites the catalog preferences of ALL existing users for this country
// with its 'new_user' default. Returns the number of affected rows.
export async function applyCountryDefaultToAllUsers(countryId: string): Promise<number> {
  const { data, error } = await (supabase.rpc as any)('apply_country_default_to_all_users', {
    target_country_id: countryId,
  });
  if (error) {
    console.error("Error applying country default to all users:", error);
    throw error;
  }
  return (data as number) ?? 0;
}

export async function createCategory(
  countryId: string,
  name: string,
  description: string,
  displayOrder: number,
  name_ar?: string,
  name_tr?: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_category_definitions')
      .insert({
        country_id: countryId,
        name,
        description,
        display_order: displayOrder,
        ...(name_ar ? { name_ar } : {}),
        ...(name_tr ? { name_tr } : {}),
      });

    if (error) {
      console.error("Error creating category:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in createCategory:", error);
    throw error;
  }
}

export async function updateCategory(
  categoryId: string,
  countryId: string,
  updates: {
    name?: string;
    description?: string;
    display_order?: number;
    name_ar?: string | null;
    name_tr?: string | null;
  }
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_category_definitions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', categoryId)
      .eq('country_id', countryId);

    if (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in updateCategory:", error);
    throw error;
  }
}

export async function deleteCategory(
  categoryId: string,
  countryId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_category_definitions')
      .delete()
      .eq('id', categoryId)
      .eq('country_id', countryId);

    if (error) {
      console.error("Error deleting category:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deleteCategory:", error);
    throw error;
  }
}

export async function createType(
  countryId: string,
  name: string,
  description: string,
  displayOrder: number,
  name_ar?: string,
  name_tr?: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_type_definitions')
      .insert({
        country_id: countryId,
        name,
        description,
        display_order: displayOrder,
        ...(name_ar ? { name_ar } : {}),
        ...(name_tr ? { name_tr } : {}),
      });

    if (error) {
      console.error("Error creating type:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in createType:", error);
    throw error;
  }
}

export async function updateType(
  typeId: string,
  countryId: string,
  updates: {
    name?: string;
    description?: string;
    display_order?: number;
    name_ar?: string | null;
    name_tr?: string | null;
  }
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_type_definitions')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', typeId)
      .eq('country_id', countryId);

    if (error) {
      console.error("Error updating type:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in updateType:", error);
    throw error;
  }
}

export async function deleteType(
  typeId: string,
  countryId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_type_definitions')
      .delete()
      .eq('id', typeId)
      .eq('country_id', countryId);

    if (error) {
      console.error("Error deleting type:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deleteType:", error);
    throw error;
  }
}

export async function createSortOption(
  countryId: string,
  name: string,
  fieldName: string,
  isDefault: boolean,
  isRequired: boolean,
  displayOrder: number,
  name_ar?: string,
  name_tr?: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_sort_options')
      .insert({
        country_id: countryId,
        name,
        field_name: fieldName,
        is_default: isDefault,
        is_required: isRequired,
        display_order: displayOrder,
        ...(name_ar ? { name_ar } : {}),
        ...(name_tr ? { name_tr } : {}),
      });

    if (error) {
      console.error("Error creating sort option:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in createSortOption:", error);
    throw error;
  }
}

export async function updateSortOption(
  id: string,
  countryId: string,
  data: {
    name?: string;
    field_name?: string;
    description?: string;
    is_default?: boolean;
    is_required?: boolean;
    display_order?: number;
    name_ar?: string | null;
    name_tr?: string | null;
  }
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('banknote_sort_options')
      .update(data)
      .eq('id', id)
      .eq('country_id', countryId);

    if (error) {
      console.error('Error updating sort option:', error);
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Error in updateSortOption:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
}

export async function deleteSortOption(
  sortOptionId: string,
  countryId: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('banknote_sort_options')
      .delete()
      .eq('id', sortOptionId)
      .eq('country_id', countryId);

    if (error) {
      console.error("Error deleting sort option:", error);
      throw error;
    }
  } catch (error) {
    console.error("Error in deleteSortOption:", error);
    throw error;
  }
}

// Function to upload country images to Supabase storage
export const uploadCountryImage = async (file: File): Promise<string> => {
  console.log('Starting country image upload process:', { fileName: file.name, fileSize: file.size });
  
  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
    const filePath = `countries/${fileName}`;
    
    console.log('Generated file path:', { fileName, filePath });
    
    console.log('Attempting to upload file to Supabase storage...');
    const { data, error } = await supabase.storage
      .from('banknote_images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });
      
    if (error) {
      console.error('Supabase storage upload error:', error);
      throw error;
    }
    
    console.log('File uploaded successfully:', data);

    console.log('Getting public URL for uploaded file...');
    const { data: urlData } = supabase.storage
      .from('banknote_images')
      .getPublicUrl(data.path);
      
    console.log('Generated public URL:', urlData);
    return urlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadCountryImage:', error);
    throw new Error('Failed to upload image');
  }
};
